import { useCallback, useEffect, useState } from 'react';
import { fitLiveApi } from './api';
import './styles/fit-live.css';

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Inicio del creator Fit Live: estadísticas de publicaciones + filtro de fechas.
 */
export default function FitLiveCreatorHome({ onOpenStudio }) {
  const [from, setFrom] = useState(() => daysAgoIso(30));
  const [to, setTo] = useState(() => todayIso());
  const [data, setData] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [me, stats] = await Promise.all([
        fitLiveApi.me().catch(() => null),
        fitLiveApi.creatorHomeStats({ from, to }),
      ]);
      setCreator(me?.creator || null);
      setData(stats);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las estadísticas');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const rows = data?.classes || [];

  return (
    <div className="fit-live-root surface-readable" data-testid="fit-live-creator-home">
      <div className="fl-shell" style={{ maxWidth: 1100, margin: '0 auto', padding: '1rem 1.25rem 2rem' }}>
        <header className="fl-section-head" style={{ marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
              {creator?.display_name || 'Creator'}
              {' · '}
              Inicio
            </h1>
            <p className="fl-note" style={{ margin: '0.35rem 0 0' }}>
              Publicaciones, ingresos a clases y vistas de replay en el rango elegido.
            </p>
          </div>
          <button type="button" className="fl-btn fl-btn-primary" onClick={() => onOpenStudio?.()}>
            Ir a Creator Studio
          </button>
        </header>

        <section className="fl-kpi-grid" style={{ marginBottom: '1rem' }}>
          <label className="fl-kpi">
            <span>Desde</span>
            <input className="fl-field" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="fl-kpi">
            <span>Hasta</span>
            <input className="fl-field" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="fl-kpi" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="fl-btn" disabled={loading} onClick={load}>
              Actualizar
            </button>
          </div>
        </section>

        {error ? <p className="fl-note" style={{ color: '#b91c1c' }}>{error}</p> : null}

        {loading && !data ? (
          <p className="fl-note">Cargando estadísticas…</p>
        ) : (
          <>
            <section className="fl-kpi-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="fl-kpi"><label>Publicadas</label><strong>{s.published || 0}</strong></div>
              <div className="fl-kpi"><label>Borradores</label><strong>{s.drafts || 0}</strong></div>
              <div className="fl-kpi"><label>Reservas</label><strong>{s.reservations || 0}</strong></div>
              <div className="fl-kpi"><label>Ingresos (asistieron)</label><strong>{s.present || 0}</strong></div>
              <div className="fl-kpi"><label>Vistas replay</label><strong>{s.replay_views || 0}</strong></div>
              <div className="fl-kpi"><label>Total entradas</label><strong>{s.entries || 0}</strong></div>
            </section>

            <section>
              <div className="fl-section-head">
                <div>
                  <h2>Tus publicaciones</h2>
                  <p>Filtradas por fecha de clase o publicación</p>
                </div>
              </div>
              <div className="fl-table-wrap">
                <table className="fl-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Clase</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Reservas</th>
                      <th>Ingresos</th>
                      <th>No-show</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.class_id}>
                        <td style={{ width: 56 }}>
                          {r.image_url ? (
                            <img
                              src={r.image_url}
                              alt=""
                              style={{
                                width: 48, height: 36, objectFit: 'cover', borderRadius: 8,
                              }}
                            />
                          ) : (
                            <div style={{
                              width: 48, height: 36, borderRadius: 8, background: '#e7e5e4',
                            }}
                            />
                          )}
                        </td>
                        <td>{r.title}</td>
                        <td>
                          {r.fecha || '—'}
                          {r.hora ? ` · ${r.hora}` : ''}
                        </td>
                        <td>{r.status === 'published' ? 'Publicada' : 'Borrador'}</td>
                        <td>{r.reserved}</td>
                        <td>{r.present}</td>
                        <td>{r.noshow}</td>
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr>
                        <td colSpan={7}>Sin clases en este rango. Crea o publica en Creator Studio.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
